"use client"

import { useMemo, useState, useSyncExternalStore } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Copy, RefreshCw, Clock, Calendar, AlertCircle, Play } from "lucide-react"
import { toast } from "sonner"

function getNextCronRuns(cronExpression: string, count: number = 5): Date[] {
  const parts = cronExpression.trim().split(/\s+/)
  if (parts.length !== 5) return []

  const [minPart, hourPart, domPart, monPart, dowPart] = parts

  function parseField(field: string, min: number, max: number): number[] {
    if (field === "*") return Array.from({ length: max - min + 1 }, (_, i) => i + min)
    if (field.includes("/")) {
      const [base, stepStr] = field.split("/")
      const step = parseInt(stepStr)
      const start = base === "*" ? min : parseInt(base)
      const values: number[] = []
      for (let i = start; i <= max; i += step) values.push(i)
      return values
    }
    if (field.includes("-")) {
      const [startStr, endStr] = field.split("-")
      const start = parseInt(startStr)
      const end = parseInt(endStr)
      const values: number[] = []
      for (let i = start; i <= end; i++) values.push(i)
      return values
    }
    if (field.includes(",")) {
      return field.split(",").map((v) => parseInt(v.trim()))
    }
    return [parseInt(field)]
  }

  try {
    const minutes = parseField(minPart, 0, 59)
    const hours = parseField(hourPart, 0, 23)
    const daysOfMonth = parseField(domPart, 1, 31)
    const months = parseField(monPart, 1, 12)
    const daysOfWeek = parseField(dowPart, 0, 6)

    const results: Date[] = []
    const now = new Date()
    const candidate = new Date(now)
    candidate.setSeconds(0)
    candidate.setMilliseconds(0)
    candidate.setMinutes(candidate.getMinutes() + 1)

    const maxIterations = 525960 // 1 year in minutes
    let iterations = 0

    while (results.length < count && iterations < maxIterations) {
      iterations++
      const m = candidate.getMinutes()
      const h = candidate.getHours()
      const dom = candidate.getDate()
      const mon = candidate.getMonth() + 1
      const dow = candidate.getDay()

      if (
        minutes.includes(m) &&
        hours.includes(h) &&
        months.includes(mon) &&
        (domPart === "*" || daysOfMonth.includes(dom)) &&
        (dowPart === "*" || daysOfWeek.includes(dow))
      ) {
        results.push(new Date(candidate))
      }

      candidate.setMinutes(candidate.getMinutes() + 1)
    }

    return results
  } catch {
    return []
  }
}

const CRON_PATTERN = /^(\*|([0-9]|1[0-9]|2[0-9]|3[0-9]|4[0-9]|5[0-9])|\*\/([0-9]|1[0-9]|2[0-9]|3[0-9]|4[0-9]|5[0-9])|([0-9]|1[0-9]|2[0-9]|3[0-9]|4[0-9]|5[0-9])-([0-9]|1[0-9]|2[0-9]|3[0-9]|4[0-9]|5[0-9])|([0-9]|1[0-9]|2[0-9]|3[0-9]|4[0-9]|5[0-9]),([0-9]|1[0-9]|2[0-3])) (\*|([0-9]|1[0-9]|2[0-3])|\*\/([0-9]|1[0-9]|2[0-3])|([0-9]|1[0-9]|2[0-3])-([0-9]|1[0-9]|2[0-3])|([0-9]|1[0-9]|2[0-3]),([0-9]|1[0-9]|2[0-3])) (\*|([1-9]|1[0-9]|2[0-9]|3[0-1])|\*\/([1-9]|1[0-9]|2[0-9]|3[0-1])|([1-9]|1[0-9]|2[0-9]|3[0-1])-([1-9]|1[0-9]|2[0-9]|3[0-1])|([1-9]|1[0-9]|2[0-9]|3[0-1]),([1-9]|1[0-9]|2[0-9]|3[0-1])) (\*|([1-9]|1[0-2])|\*\/([1-9]|1[0-2])|([1-9]|1[0-2])-([1-9]|1[0-2])|([1-9]|1[0-2]),([1-9]|1[0-2])) (\*|([0-6])|\*\/([0-6])|([0-6])-([0-6])|([0-6]),([0-6]))$/
const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"]
const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"]
const subscribeToHydration = () => () => {}

function validateCronExpression(expression: string) {
  const parts = expression.trim().split(/\s+/)
  if (parts.length !== 5) {
    return { isValid: false, validationMessage: "Cron expression must have 5 parts" }
  }
  if (!CRON_PATTERN.test(expression)) {
    return { isValid: false, validationMessage: "Invalid cron expression format" }
  }
  if (parts[2] !== "*" && parts[4] !== "*") {
    return {
      isValid: false,
      validationMessage: "Cannot specify both day-of-month and day-of-week (one must be *)",
    }
  }
  return { isValid: true, validationMessage: "" }
}

function getHumanReadable(expression: string): string {
  const parts = expression.trim().split(/\s+/)
  if (parts.length !== 5) return ""

  const [minute, hour, dayOfMonth, month, dayOfWeek] = parts
  if (expression === "* * * * *") return "Every minute"
  if (expression === "0 * * * *") return "Every hour at minute 0"
  if (expression === "0 0 * * *") return "Every day at 12:00 AM"
  if (expression === "0 12 * * *") return "Every day at 12:00 PM"
  if (expression === "0 0 * * 0") return "Every Sunday at 12:00 AM"
  if (expression === "0 0 * * 1-5") return "Every weekday at 12:00 AM"

  let timeDescription = ""
  if (minute === "*" && hour === "*") {
    timeDescription = "every minute"
  } else if (minute === "0" && hour === "*") {
    timeDescription = "every hour on the hour"
  } else if (minute !== "*" && hour === "*") {
    timeDescription = `every hour at minute ${minute}`
  } else if (minute === "*" && hour !== "*") {
    timeDescription = `every minute during hour ${hour}`
  } else {
    const numericHour = Number.parseInt(hour)
    const formattedHour = numericHour === 0
      ? "12 AM"
      : numericHour === 12
        ? "12 PM"
        : numericHour > 12
          ? `${numericHour - 12} PM`
          : `${numericHour} AM`
    timeDescription = `at ${formattedHour}:${minute.padStart(2, "0")}`
  }

  let dayDescription = ""
  if (dayOfMonth === "*" && month === "*" && dayOfWeek === "*") {
    dayDescription = "every day"
  } else if (dayOfMonth !== "*" && month === "*" && dayOfWeek === "*") {
    dayDescription = `on day ${dayOfMonth} of every month`
  } else if (dayOfMonth === "*" && month !== "*" && dayOfWeek === "*") {
    dayDescription = `every day in ${MONTH_NAMES.filter((_, index) => month.includes((index + 1).toString())).join(", ")}`
  } else if (dayOfMonth === "*" && month === "*" && dayOfWeek !== "*") {
    dayDescription = `every ${DAY_NAMES.filter((_, index) => dayOfWeek.includes(index.toString())).join(", ")}`
  } else if (dayOfMonth !== "*" && month !== "*" && dayOfWeek === "*") {
    dayDescription = `on day ${dayOfMonth} of ${MONTH_NAMES.filter((_, index) => month.includes((index + 1).toString())).join(", ")}`
  }

  return `Runs ${timeDescription} ${dayDescription}`
}

export function CronBuilder() {
  const [cronExpression, setCronExpression] = useState("* * * * *")
  const [minute, setMinute] = useState("*")
  const [hour, setHour] = useState("*")
  const [dayOfMonth, setDayOfMonth] = useState("*")
  const [month, setMonth] = useState("*")
  const [dayOfWeek, setDayOfWeek] = useState("*")
  const [activeTab, setActiveTab] = useState("builder")

  const predefinedSchedules = [
    { label: "@yearly", value: "0 0 1 1 *", description: "Once a year (Jan 1st)" },
    { label: "@monthly", value: "0 0 1 * *", description: "Once a month (1st day)" },
    { label: "@weekly", value: "0 0 * * 0", description: "Once a week (Sunday)" },
    { label: "@daily", value: "0 0 * * *", description: "Once a day (midnight)" },
    { label: "@hourly", value: "0 * * * *", description: "Once an hour" },
    { label: "Every minute", value: "* * * * *", description: "Every single minute" },
    { label: "Every day at noon", value: "0 12 * * *", description: "12:00 PM daily" },
    { label: "Every weekday", value: "0 0 * * 1-5", description: "Mon-Fri at midnight" },
    { label: "Every weekend", value: "0 0 * * 0,6", description: "Sat & Sun at midnight" },
    { label: "Every quarter", value: "0 0 1 1,4,7,10 *", description: "Jan, Apr, Jul, Oct 1st" },
  ]

  // Generate options for select inputs
  const generateOptions = (start: number, end: number) => {
    return Array.from({ length: end - start + 1 }, (_, i) => i + start)
  }

  const minuteOptions = generateOptions(0, 59)
  const hourOptions = generateOptions(0, 23)
  const dayOfMonthOptions = generateOptions(1, 31)
  const monthOptions = [
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" },
  ]
  const dayOfWeekOptions = [
    { value: 0, label: "Sunday" },
    { value: 1, label: "Monday" },
    { value: 2, label: "Tuesday" },
    { value: 3, label: "Wednesday" },
    { value: 4, label: "Thursday" },
    { value: 5, label: "Friday" },
    { value: 6, label: "Saturday" },
  ]

  const syncFieldsFromExpression = (expression: string) => {
    const parts = expression.trim().split(/\s+/)
    if (parts.length !== 5) return

    setMinute(parts[0])
    setHour(parts[1])
    setDayOfMonth(parts[2])
    setMonth(parts[3])
    setDayOfWeek(parts[4])
  }

  const updateBuilderField = (
    field: "minute" | "hour" | "dayOfMonth" | "month" | "dayOfWeek",
    value: string
  ) => {
    const nextFields = { minute, hour, dayOfMonth, month, dayOfWeek, [field]: value }
    if (field === "minute") setMinute(value)
    if (field === "hour") setHour(value)
    if (field === "dayOfMonth") setDayOfMonth(value)
    if (field === "month") setMonth(value)
    if (field === "dayOfWeek") setDayOfWeek(value)
    setCronExpression(
      `${nextFields.minute} ${nextFields.hour} ${nextFields.dayOfMonth} ${nextFields.month} ${nextFields.dayOfWeek}`
    )
  }

  const handleExpressionChange = (value: string) => {
    setCronExpression(value)
    syncFieldsFromExpression(value)
  }

  const handleTabChange = (value: string) => {
    setActiveTab(value)
    if (value === "builder") {
      setCronExpression(`${minute} ${hour} ${dayOfMonth} ${month} ${dayOfWeek}`)
    }
  }

  const { isValid, validationMessage } = validateCronExpression(cronExpression)
  const humanReadable = isValid ? getHumanReadable(cronExpression) : ""
  const isHydrated = useSyncExternalStore(subscribeToHydration, () => true, () => false)
  const nextRuns = useMemo(
    () => (isHydrated && isValid ? getNextCronRuns(cronExpression, 5) : []),
    [cronExpression, isHydrated, isValid]
  )

  // Handle predefined schedule selection
  const handlePredefinedSchedule = (value: string) => {
    setCronExpression(value)
    syncFieldsFromExpression(value)
    setActiveTab("expression")
  }

  // Copy cron expression to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(cronExpression)
    toast.success("Cron expression copied to clipboard!")
  }

  // Generate a random cron expression
  const generateRandomCron = () => {
    const randomMinute = Math.floor(Math.random() * 60).toString()
    const randomHour = Math.floor(Math.random() * 24).toString()
    const randomDayOfMonth = Math.floor(Math.random() * 28 + 1).toString()
    const randomMonth = Math.floor(Math.random() * 12 + 1).toString()
    const randomDayOfWeek = Math.floor(Math.random() * 7).toString()

    // Randomly decide which fields to use * for
    const fields = [randomMinute, randomHour, randomDayOfMonth, randomMonth, randomDayOfWeek]
    const useAsterisk = Array.from({ length: 5 }, () => Math.random() > 0.7)

    // Ensure we don't specify both day-of-month and day-of-week
    if (!useAsterisk[2] && !useAsterisk[4]) {
      useAsterisk[Math.random() > 0.5 ? 2 : 4] = true
    }

    const randomCron = fields.map((field, index) => (useAsterisk[index] ? "*" : field)).join(" ")
    setCronExpression(randomCron)
    syncFieldsFromExpression(randomCron)
    setActiveTab("expression")
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Cron Expression Builder</CardTitle>
        <CardDescription>
          Create and validate cron expressions for scheduled tasks. Use the builder or enter expressions directly.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList className="grid grid-cols-2">
            <TabsTrigger value="builder">Visual Builder</TabsTrigger>
            <TabsTrigger value="expression">Expression</TabsTrigger>
          </TabsList>

          <TabsContent value="builder" className="space-y-6 pt-4">
            <div className="grid gap-6 md:grid-cols-5">
              <div className="space-y-2">
                <Label htmlFor="minute">Minute</Label>
                <Select value={minute} onValueChange={(value) => updateBuilderField("minute", value)}>
                  <SelectTrigger id="minute">
                    <SelectValue placeholder="Minute" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="*">Every minute (*)</SelectItem>
                    {minuteOptions.map((m) => (
                      <SelectItem key={`minute-${m}`} value={m.toString()}>
                        {m.toString().padStart(2, "0")}
                      </SelectItem>
                    ))}
                    <SelectItem value="*/5">Every 5 minutes (*/5)</SelectItem>
                    <SelectItem value="*/10">Every 10 minutes (*/10)</SelectItem>
                    <SelectItem value="*/15">Every 15 minutes (*/15)</SelectItem>
                    <SelectItem value="*/30">Every 30 minutes (*/30)</SelectItem>
                    <SelectItem value="0,30">0 and 30 (0,30)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="hour">Hour</Label>
                <Select value={hour} onValueChange={(value) => updateBuilderField("hour", value)}>
                  <SelectTrigger id="hour">
                    <SelectValue placeholder="Hour" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="*">Every hour (*)</SelectItem>
                    {hourOptions.map((h) => (
                      <SelectItem key={`hour-${h}`} value={h.toString()}>
                        {h.toString().padStart(2, "0")}{" "}
                        {h === 0 ? "(12 AM)" : h === 12 ? "(12 PM)" : h > 12 ? `(${h - 12} PM)` : `(${h} AM)`}
                      </SelectItem>
                    ))}
                    <SelectItem value="*/2">Every 2 hours (*/2)</SelectItem>
                    <SelectItem value="*/3">Every 3 hours (*/3)</SelectItem>
                    <SelectItem value="*/4">Every 4 hours (*/4)</SelectItem>
                    <SelectItem value="*/6">Every 6 hours (*/6)</SelectItem>
                    <SelectItem value="*/12">Every 12 hours (*/12)</SelectItem>
                    <SelectItem value="9-17">Business hours (9-17)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="day-of-month">Day of Month</Label>
                <Select value={dayOfMonth} onValueChange={(value) => updateBuilderField("dayOfMonth", value)}>
                  <SelectTrigger id="day-of-month">
                    <SelectValue placeholder="Day of Month" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="*">Every day (*)</SelectItem>
                    {dayOfMonthOptions.map((d) => (
                      <SelectItem key={`dom-${d}`} value={d.toString()}>
                        {d}
                      </SelectItem>
                    ))}
                    <SelectItem value="*/2">Every 2 days (*/2)</SelectItem>
                    <SelectItem value="*/5">Every 5 days (*/5)</SelectItem>
                    <SelectItem value="*/10">Every 10 days (*/10)</SelectItem>
                    <SelectItem value="1,15">1st and 15th (1,15)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="month">Month</Label>
                <Select value={month} onValueChange={(value) => updateBuilderField("month", value)}>
                  <SelectTrigger id="month">
                    <SelectValue placeholder="Month" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="*">Every month (*)</SelectItem>
                    {monthOptions.map((m) => (
                      <SelectItem key={`month-${m.value}`} value={m.value.toString()}>
                        {m.label}
                      </SelectItem>
                    ))}
                    <SelectItem value="*/3">Every quarter (*/3)</SelectItem>
                    <SelectItem value="*/6">Every half year (*/6)</SelectItem>
                    <SelectItem value="1,6">January and June (1,6)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="day-of-week">Day of Week</Label>
                <Select value={dayOfWeek} onValueChange={(value) => updateBuilderField("dayOfWeek", value)}>
                  <SelectTrigger id="day-of-week">
                    <SelectValue placeholder="Day of Week" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="*">Every day (*)</SelectItem>
                    {dayOfWeekOptions.map((d) => (
                      <SelectItem key={`dow-${d.value}`} value={d.value.toString()}>
                        {d.label}
                      </SelectItem>
                    ))}
                    <SelectItem value="1-5">Weekdays (1-5)</SelectItem>
                    <SelectItem value="0,6">Weekends (0,6)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="expression" className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label htmlFor="cron-expression">Cron Expression</Label>
              <div className="flex gap-2">
                <Input
                  id="cron-expression"
                  value={cronExpression}
                  onChange={(e) => handleExpressionChange(e.target.value)}
                  placeholder="* * * * *"
                  className="font-mono"
                />
                <Button variant="outline" size="icon" onClick={copyToClipboard}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">Format: minute hour day-of-month month day-of-week</p>
            </div>

            <div className="space-y-2">
              <Label>Predefined Schedules</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {predefinedSchedules.map((schedule) => (
                  <Button
                    key={schedule.value}
                    variant="outline"
                    size="sm"
                    className="justify-start h-auto py-2"
                    onClick={() => handlePredefinedSchedule(schedule.value)}
                  >
                    <Clock className="h-4 w-4 mr-2 flex-shrink-0" />
                    <div className="text-left">
                      <div>{schedule.label}</div>
                      <div className="text-xs text-muted-foreground font-normal">{schedule.description}</div>
                    </div>
                  </Button>
                ))}
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <div className="flex flex-col gap-4 pt-4 border-t">
          <div className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-primary" />
            <span className="font-medium">Expression:</span>
            <code className="bg-muted px-2 py-1 rounded font-mono">{cronExpression}</code>
          </div>

          {isValid ? (
            <div className="bg-primary/10 text-primary p-3 rounded-md">
              <p className="font-medium">Human-readable:</p>
              <p>{humanReadable}</p>
            </div>
          ) : (
            <div className="bg-destructive/10 text-destructive p-3 rounded-md flex items-start gap-2">
              <AlertCircle className="h-5 w-5 mt-0.5" />
              <div>
                <p className="font-medium">Invalid cron expression</p>
                <p>{validationMessage}</p>
              </div>
            </div>
          )}
        </div>

        {isValid && nextRuns.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Play className="h-4 w-4 text-primary" />
              <span className="font-medium text-sm">Next Executions</span>
            </div>
            <div className="border rounded-lg divide-y">
              {nextRuns.map((date, i) => (
                <div key={i} className="px-3 py-2 text-sm flex items-center justify-between">
                  <span className="text-muted-foreground">#{i + 1}</span>
                  <span className="font-mono">
                    {date.toLocaleString(undefined, {
                      weekday: "short",
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex justify-between pt-4">
          <Button variant="outline" onClick={generateRandomCron}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Generate Random
          </Button>

          <Button onClick={copyToClipboard} disabled={!isValid}>
            <Copy className="h-4 w-4 mr-2" />
            Copy Expression
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

